import { useState, useEffect } from 'react';
import { ChevronRight, User, Mail, Globe, Lock, Trash2 } from 'lucide-react';
import { useApp } from '../lib/AppContext';
import { useAuth } from '../lib/AuthContext';
import { useEffectiveUser } from '../hooks/useEffectiveUser';
import { updateProfile } from '../lib/civicbeatData';
import { supabase } from '../lib/supabaseClient';
import Header from '../components/Header';
import CountrySelect from '../components/CountrySelect';
import Button from '../components/Button';

export default function ProfileSettings() {
  const { navigateTo, showToast } = useApp();
  const { refreshProfile } = useAuth();
  const { user, profile, isLoggedOut, isPreviewMode } = useEffectiveUser();
  const [name, setName] = useState('');
  const [country, setCountry] = useState('');
  const [isSaving, setIsSaving] = useState(false);
  const [hasChanges, setHasChanges] = useState(false);

  useEffect(() => {
    if (isLoggedOut || !user) {
      navigateTo('settings');
    }
  }, [isLoggedOut, user, navigateTo]);

  useEffect(() => {
    if (profile) {
      setName(profile.display_name || '');
      setCountry(profile.country || '');
    }
  }, [profile]);

  useEffect(() => {
    if (!profile) return;

    const hasNameChanged = name !== (profile.display_name || '');
    const hasCountryChanged = country !== (profile.country || '');

    setHasChanges(hasNameChanged || hasCountryChanged);
  }, [name, country, profile]);

  const handleSaveChanges = async () => {
    if (!user || !hasChanges) return;

    if (isPreviewMode) {
      showToast('Preview mode: Changes not saved');
      return;
    }

    setIsSaving(true);
    try {
      const updates: { display_name?: string; country?: string } = {};

      if (name !== (profile?.display_name || '')) {
        updates.display_name = name;
      }

      if (country !== (profile?.country || '')) {
        updates.country = country;
      }

      console.log('[ProfileSettings] Saving updates:', updates);

      // Update profile table
      const result = await updateProfile(user.id, updates);
      console.log('[ProfileSettings] Update result:', result);

      if (result) {
        // Also update auth metadata so it shows in Supabase Dashboard
        if (updates.display_name !== undefined) {
          const { error: authError } = await supabase.auth.updateUser({
            data: { display_name: updates.display_name }
          });
          if (authError) {
            console.error('[ProfileSettings] Error updating auth metadata:', authError);
          }
        }

        await refreshProfile();
        showToast('Profile updated');
        setHasChanges(false);
      } else {
        showToast('Failed to update profile');
      }
    } catch (error) {
      console.error('Error saving profile:', error);
      showToast('An error occurred while saving');
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#111111] pb-20">
      <Header
        mode="title"
        title="Profile & Account Settings"
        showBackButton
        onBackClick={() => navigateTo('settings')}
      />

      <div className="px-6 py-6">
        <div className="max-w-[600px] mx-auto space-y-7">
          {/* PROFILE SECTION */}
          <div>
            <div className="text-[11px] font-medium text-[#707070] uppercase tracking-wider mb-4">
              Profile
            </div>
            <div className="bg-[#181818] rounded-xl border border-[#282828]" style={{ boxShadow: '0px 12px 28px 3px rgba(0, 0, 0, 0.52), 0px 2px 8px 1px rgba(0, 0, 0, 0.16), inset 0px 1px 1px rgba(255, 255, 255, 0.04), inset 0px -1px 2px rgba(0, 0, 0, 0.22)' }}>
              {/* Name field */}
              <div className="px-5 py-4 border-b border-[#111111]">
                <div className="flex items-center gap-2.5 mb-2.5">
                  <User className="w-4 h-4 text-[#50B3A8]" />
                  <label className="text-[12px] text-[#909090]">Name</label>
                </div>
                <input
                  type="text"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="Enter your name"
                  disabled={isSaving}
                  className="w-full bg-black/30 text-[#F5F5F5] rounded-lg px-3.5 py-2.5 text-[15px] border border-[#282828] hover:border-[#383838] focus:border-[#40E0D0] focus:ring-1 focus:ring-[#40E0D0]/15 focus:outline-none transition-all disabled:opacity-50"
                />
              </div>

              {/* Email field (read-only) */}
              <div className="px-5 py-4 border-b border-[#111111]">
                <div className="flex items-center gap-2.5 mb-2.5">
                  <Mail className="w-4 h-4 text-[#50B3A8]" />
                  <label className="text-[12px] text-[#909090]">Email</label>
                </div>
                <div
                  className="w-full bg-[#0F0F0F] text-[#909090] rounded-lg px-3.5 py-2.5 text-[15px] border border-[#1A1A1A]"
                >
                  {user?.email || ''}
                </div>
                <p className="text-[11px] text-[#606060] mt-2 pl-3.5 leading-relaxed">
                  Used for sign-in and important account updates
                </p>
              </div>

              {/* Country/Region field */}
              <div className="px-5 py-4">
                <div className="flex items-center gap-2.5 mb-2.5">
                  <Globe className="w-4 h-4 text-[#50B3A8]" />
                  <label className="text-[12px] text-[#909090]">Country/Region</label>
                </div>
                <CountrySelect
                  value={country}
                  onChange={setCountry}
                  placeholder="Select your country"
                />
              </div>
            </div>
          </div>

          {/* SECURITY SECTION */}
          <div>
            <div className="text-[11px] font-medium text-[#707070] uppercase tracking-wider mb-4">
              Security
            </div>
            <div className="bg-[#181818] rounded-xl border border-[#282828] overflow-hidden" style={{ boxShadow: '0px 12px 28px 3px rgba(0, 0, 0, 0.52), 0px 2px 8px 1px rgba(0, 0, 0, 0.16), inset 0px 1px 1px rgba(255, 255, 255, 0.04), inset 0px -1px 2px rgba(0, 0, 0, 0.22)' }}>
              <button
                className="w-full px-5 py-3.5 flex items-center justify-between border-b border-[#111111] transition-all hover:bg-[#1A1A1A] group"
                onClick={() => navigateTo('change-email')}
              >
                <div className="flex items-start gap-2.5">
                  <Mail className="w-4 h-4 text-[#50B3A8] transition-colors group-hover:text-[#40E0D0] mt-[3px]" />
                  <div className="text-left">
                    <div className="text-[14px] text-[#F5F5F5]">Change email</div>
                    <div className="text-[11px] text-[#606060] mt-0.5">Requires password confirmation</div>
                  </div>
                </div>
                <ChevronRight className="w-4 h-4 text-[#50B3A8] transition-colors group-hover:text-[#40E0D0]" />
              </button>
              <button
                className="w-full px-5 py-3.5 flex items-center justify-between transition-all hover:bg-[#1A1A1A] group"
                onClick={() => navigateTo('change-password')}
              >
                <div className="flex items-center gap-2.5">
                  <Lock className="w-4 h-4 text-[#50B3A8] transition-colors group-hover:text-[#40E0D0]" />
                  <span className="text-[14px] text-[#F5F5F5]">Change password</span>
                </div>
                <ChevronRight className="w-4 h-4 text-[#50B3A8] transition-colors group-hover:text-[#40E0D0]" />
              </button>
            </div>
          </div>

          {/* ACCOUNT SECTION */}
          <div className="pt-2">
            <div className="text-[11px] font-medium text-[#707070] uppercase tracking-wider mb-4">
              Account
            </div>
            <div className="bg-[#181818] rounded-xl border border-[#282828] overflow-hidden" style={{ boxShadow: '0px 12px 28px 3px rgba(0, 0, 0, 0.52), 0px 2px 8px 1px rgba(0, 0, 0, 0.16), inset 0px 1px 1px rgba(255, 255, 255, 0.04), inset 0px -1px 2px rgba(0, 0, 0, 0.22)' }}>
              <button
                className="w-full px-5 py-3.5 flex items-center justify-between transition-colors hover:bg-[#1A1A1A]"
                onClick={() => navigateTo('delete-account')}
              >
                <div className="flex items-center gap-2.5">
                  <Trash2 className="w-4 h-4 text-[#FF6F61]" />
                  <span className="text-[14px] text-[#FF6F61]">Delete account</span>
                </div>
                <ChevronRight className="w-4 h-4 text-[#FF6F61]/60" />
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Save Changes Button - Fixed at bottom */}
      {hasChanges && (
        <div className="fixed bottom-0 left-0 right-0 bg-[#111111] border-t border-[#181818] p-4 animate-in slide-in-from-bottom duration-200">
          <div className="max-w-[600px] mx-auto px-2">
            <Button
              fullWidth
              onClick={handleSaveChanges}
              disabled={isSaving}
              variant="primary"
            >
              {isSaving ? 'Saving...' : 'Save Changes'}
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
